import {
  FuelAlertKind,
  FuelAlertLatency,
  FuelCalibrationResult,
  FuelClaimStatus,
  FuelDataMode,
  FuelDeliveryVerificationStatus,
  FuelDispenseAuthStatus,
  FuelDispenseMethod,
  FuelDrawerClass,
  FuelDrumStatus,
  FuelLossCaseStatus,
  FuelLossOutcome,
  FuelLossRootCause,
  FuelLossSeverity,
  FuelMachineAnalyticsClass,
  FuelRateProvenance,
  FuelReconStatus,
  FuelTankClass,
  FuelTransferLineDirection,
} from 'types/fuelEnums.interface'

export const DELIVERY_VERIFICATION_TO_UI: {
  [k: number]: 'VERIFIED' | 'SHORT' | 'UNVERIFIED'
} = {
  [FuelDeliveryVerificationStatus.Verified]: 'VERIFIED',
  [FuelDeliveryVerificationStatus.Short]: 'SHORT',
  [FuelDeliveryVerificationStatus.Unverified]: 'UNVERIFIED',
}

export const CLAIM_STATUS_TO_UI: { [k: number]: 'OPEN' | 'SETTLED' | 'REJECTED' } = {
  [FuelClaimStatus.Open]: 'OPEN',
  [FuelClaimStatus.Settled]: 'SETTLED',
  [FuelClaimStatus.Rejected]: 'REJECTED',
}

export const CLAIM_STATUS_TO_API: { [k in 'SETTLED' | 'REJECTED']: FuelClaimStatus } = {
  SETTLED: FuelClaimStatus.Settled,
  REJECTED: FuelClaimStatus.Rejected,
}

export const DISPENSE_METHOD_TO_UI: { [k: number]: 'MACHINE' | 'DRUM' } = {
  [FuelDispenseMethod.Machine]: 'MACHINE',
  [FuelDispenseMethod.Drum]: 'DRUM',
}

export const DISPENSE_AUTH_TO_UI: { [k: number]: 'AUTHORIZED' | 'UNAUTHORIZED' } = {
  [FuelDispenseAuthStatus.Authorized]: 'AUTHORIZED',
  [FuelDispenseAuthStatus.Unauthorized]: 'UNAUTHORIZED',
}

export const DRAWER_CLASS_TO_UI: { [k: number]: string } = {
  [FuelDrawerClass.HaulTrucksMobilePlant]: 'Haul trucks & mobile plant',
  [FuelDrawerClass.LightVehicles]: 'Light vehicles',
  [FuelDrawerClass.DrumsStaticPlant]: 'Drums & static plant',
}

export const DRUM_STATUS_TO_UI: { [k: number]: 'OPEN' | 'CLOSED' } = {
  [FuelDrumStatus.Open]: 'OPEN',
  [FuelDrumStatus.Closed]: 'CLOSED',
}

export const LOSS_CASE_STATUS_TO_UI: {
  [k: number]: 'OPEN' | 'PENDING_APPROVAL' | 'CLOSED' | 'UNDER_INVESTIGATION'
} = {
  [FuelLossCaseStatus.Open]: 'OPEN',
  [FuelLossCaseStatus.PendingApproval]: 'PENDING_APPROVAL',
  [FuelLossCaseStatus.Closed]: 'CLOSED',
  [FuelLossCaseStatus.UnderInvestigation]: 'UNDER_INVESTIGATION',
}

export const LOSS_CASE_STATUS_TO_API: {
  [k in 'OPEN' | 'PENDING_APPROVAL' | 'CLOSED' | 'UNDER_INVESTIGATION']: FuelLossCaseStatus
} = {
  OPEN: FuelLossCaseStatus.Open,
  PENDING_APPROVAL: FuelLossCaseStatus.PendingApproval,
  CLOSED: FuelLossCaseStatus.Closed,
  UNDER_INVESTIGATION: FuelLossCaseStatus.UnderInvestigation,
}

export const LOSS_OUTCOME_TO_UI: { [k: number]: 'RECOVERED' | 'WRITTEN_OFF' | 'DISCIPLINARY' } = {
  [FuelLossOutcome.Recovered]: 'RECOVERED',
  [FuelLossOutcome.WrittenOff]: 'WRITTEN_OFF',
  [FuelLossOutcome.Disciplinary]: 'DISCIPLINARY',
}

export const LOSS_OUTCOME_TO_API: { [k in 'RECOVERED' | 'WRITTEN_OFF' | 'DISCIPLINARY']: FuelLossOutcome } = {
  RECOVERED: FuelLossOutcome.Recovered,
  WRITTEN_OFF: FuelLossOutcome.WrittenOff,
  DISCIPLINARY: FuelLossOutcome.Disciplinary,
}

export const LOSS_ROOT_CAUSE_TO_UI: {
  [k: number]:
    | 'METER_OR_DISPENSER_DRIFT'
    | 'DIP_OR_MEASUREMENT_ERROR'
    | 'DATA_ENTRY_ERROR'
    | 'LEAK_OR_SPILLAGE'
    | 'THEFT_OR_UNAUTHORIZED_DRAW'
    | 'UNRECORDED_DISPENSE'
    | 'TEMPERATURE_OR_EVAPORATION'
    | 'OTHER'
} = {
  [FuelLossRootCause.MeterOrDispenserDrift]: 'METER_OR_DISPENSER_DRIFT',
  [FuelLossRootCause.DipOrMeasurementError]: 'DIP_OR_MEASUREMENT_ERROR',
  [FuelLossRootCause.DataEntryError]: 'DATA_ENTRY_ERROR',
  [FuelLossRootCause.LeakOrSpillage]: 'LEAK_OR_SPILLAGE',
  [FuelLossRootCause.TheftOrUnauthorizedDraw]: 'THEFT_OR_UNAUTHORIZED_DRAW',
  [FuelLossRootCause.UnrecordedDispense]: 'UNRECORDED_DISPENSE',
  [FuelLossRootCause.TemperatureOrEvaporation]: 'TEMPERATURE_OR_EVAPORATION',
  [FuelLossRootCause.Other]: 'OTHER',
}

export const LOSS_ROOT_CAUSE_TO_API: {
  [k in
    | 'METER_OR_DISPENSER_DRIFT'
    | 'DIP_OR_MEASUREMENT_ERROR'
    | 'DATA_ENTRY_ERROR'
    | 'LEAK_OR_SPILLAGE'
    | 'THEFT_OR_UNAUTHORIZED_DRAW'
    | 'UNRECORDED_DISPENSE'
    | 'TEMPERATURE_OR_EVAPORATION'
    | 'OTHER']: FuelLossRootCause
} = {
  METER_OR_DISPENSER_DRIFT: FuelLossRootCause.MeterOrDispenserDrift,
  DIP_OR_MEASUREMENT_ERROR: FuelLossRootCause.DipOrMeasurementError,
  DATA_ENTRY_ERROR: FuelLossRootCause.DataEntryError,
  LEAK_OR_SPILLAGE: FuelLossRootCause.LeakOrSpillage,
  THEFT_OR_UNAUTHORIZED_DRAW: FuelLossRootCause.TheftOrUnauthorizedDraw,
  UNRECORDED_DISPENSE: FuelLossRootCause.UnrecordedDispense,
  TEMPERATURE_OR_EVAPORATION: FuelLossRootCause.TemperatureOrEvaporation,
  OTHER: FuelLossRootCause.Other,
}

export const LOSS_SEVERITY_TO_UI: { [k: number]: 'critical' | 'warning' } = {
  [FuelLossSeverity.Critical]: 'critical',
  [FuelLossSeverity.Warning]: 'warning',
}

export const TANK_CLASS_TO_UI: { [k: number]: 'STATIC' | 'BOWSER' } = {
  [FuelTankClass.Static]: 'STATIC',
  [FuelTankClass.Bowser]: 'BOWSER',
}

export const TRANSFER_DIRECTION_TO_UI: { [k: number]: 'TRANSFER_IN' | 'TRANSFER_OUT' } = {
  [FuelTransferLineDirection.TransferIn]: 'TRANSFER_IN',
  [FuelTransferLineDirection.TransferOut]: 'TRANSFER_OUT',
}

export const RECON_STATUS_TO_UI: { [k: number]: 'DRAFT' | 'SIGNED_OFF' } = {
  [FuelReconStatus.Draft]: 'DRAFT',
  [FuelReconStatus.SignedOff]: 'SIGNED_OFF',
}

export const CALIBRATION_RESULT_TO_UI: { [k: number]: string } = {
  [FuelCalibrationResult.Pass]: 'Pass',
  [FuelCalibrationResult.Fail]: 'Fail',
}

export const ALERT_KIND_TO_UI: { [k: number]: string } = {
  [FuelAlertKind.UnauthorizedDraw]: 'Unauthorized draw',
  [FuelAlertKind.ShortDelivery]: 'Short delivery',
  [FuelAlertKind.CalibrationOverdue]: 'Calibration overdue',
  [FuelAlertKind.OverBandBurn]: 'Over-band burn',
  [FuelAlertKind.UnexplainedDrop]: 'Unexplained drop',
  [FuelAlertKind.SensorOffline]: 'Sensor offline',
  [FuelAlertKind.ExcessDispense]: 'Excess dispense',
  [FuelAlertKind.MissingSmu]: 'Missing SMU',
}

export const ALERT_LATENCY_TO_UI: { [k: number]: 'live' | 'next fill' } = {
  [FuelAlertLatency.Live]: 'live',
  [FuelAlertLatency.NextFill]: 'next fill',
}

export const DATA_MODE_TO_UI: { [k: number]: 'MANUAL' | 'TELEMETRY' } = {
  [FuelDataMode.Manual]: 'MANUAL',
  [FuelDataMode.Telemetry]: 'TELEMETRY',
}

export const MACHINE_CLASS_TO_UI: { [k: number]: 'OPERATED_MOBILE' | 'STATIC_PLANT' } = {
  [FuelMachineAnalyticsClass.OperatedMobile]: 'OPERATED_MOBILE',
  [FuelMachineAnalyticsClass.StaticPlant]: 'STATIC_PLANT',
}

export const RATE_PROVENANCE_TO_UI: { [k: number]: 'MEASURED' | 'BETWEEN_FILLS' | 'ATTRIBUTED' } = {
  [FuelRateProvenance.Measured]: 'MEASURED',
  [FuelRateProvenance.BetweenFills]: 'BETWEEN_FILLS',
  [FuelRateProvenance.Attributed]: 'ATTRIBUTED',
}
